import {
    Box,
    Checkbox,
    Container,
    FormControlLabel,
    Link,
    styled,
    Typography,
} from "@mui/material";
import React from "react";
import { Footer } from "../allComponents/AllComponents";

const PageContainer = styled(Container)(({ theme }) => ({
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(8),
    fontFamily: "'Inter', sans-serif",
}));

const Section = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(4),
}));

const Figure = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    border: "1px solid #e0e0e0",
    borderRadius: 8,
    backgroundColor: "#fafafa",
}));

const Screenshot = styled("img")({
    display: "block",
    maxWidth: "100%",
    height: "auto",
    borderRadius: 6,
    border: "1px solid #d0d0d0",
});

// A faithful, non-interactive replica of the exact consent checkbox shown to
// users in the FundsyFi Recover opt-in flow. Rendered unchecked + disabled so
// carriers / TCR reviewers can read the precise label and verify it is
// unchecked by default.
const ConsentReplica = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(3),
    border: "1px solid #d0d0d0",
    borderRadius: 8,
    backgroundColor: "#ffffff",
    maxWidth: 640,
}));

/**
 * Public, login-free page documenting how end users consent to receive SMS
 * from <b>FundsyFi Recover</b> — the accounts-receivable / invoicing product
 * that small businesses use to send transactional payment reminders, due and
 * past-due invoice notices, and payment confirmations to their own invoiced
 * customers.
 *
 * This page is referenced from the A2P 10DLC (TCR) campaign `message_flow`
 * so carriers and The Campaign Registry can publicly verify the opt-in:
 * the exact unchecked consent checkbox, the disclosure language, the Terms and
 * Privacy links, the message-frequency disclosure, the STOP/HELP instructions,
 * and a screenshot of the live flow. It is specific to FundsyFi Recover and is
 * NOT the Circle savings-group signup.
 */
const CollectSmsConsent = () => {
    return (
        <React.Fragment>
            <PageContainer maxWidth="md">
                <Typography
                    variant="h3"
                    gutterBottom
                    sx={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 600 }}
                >
                    FundsyFi Recover SMS Consent
                </Typography>
                <Typography variant="subtitle2" gutterBottom>
                    Program: FundsyFi Recover Payment Reminders &nbsp;|&nbsp; Brand:
                    FundsyFi &nbsp;|&nbsp; Last updated: June 15, 2026
                </Typography>

                <Section>
                    <Typography variant="body1" paragraph>
                        This page documents how end users consent to receive text
                        messages from <b>FundsyFi Recover</b>. FundsyFi Recover is the
                        accounts-receivable product that small businesses use to send{" "}
                        <b>transactional</b> payment reminders, due and past-due invoice
                        notices, and payment confirmations to their own invoiced
                        customers. These messages are strictly transactional FundsyFi
                        Collect never sends marketing or promotional content, and never
                        messages anyone outside an established business/billing
                        relationship.
                    </Typography>
                </Section>

                <Section>
                    <Typography variant="h5" gutterBottom>
                        Who receives messages
                    </Typography>
                    <Typography variant="body1" paragraph>
                        Recipients are existing customers of a FundsyFi Recover business
                        who have an open, due, past-due, or recently paid invoice. Before
                        any SMS is sent, the recipient must provide their mobile number
                        and give explicit consent to receive transactional invoice and
                        payment text messages. Consent is collected through a dedicated
                        SMS consent checkbox, or through an equivalent documented consent
                        process used by the FundsyFi Recover business that includes the
                        same SMS disclosures: program name, message type, message
                        frequency, message and data rates, STOP/HELP instructions, Privacy
                        Policy link, and Terms of Service link. FundsyFi Recover requires
                        the business to keep proof of consent, including consent source,
                        timestamp, customer record, business account, and phone number.
                        FundsyFi Recover does not send SMS messages to customers without
                        recorded SMS consent.
                    </Typography>
                </Section>

                <Section>
                    <Typography variant="h5" gutterBottom>
                        The exact consent checkbox
                    </Typography>
                    <Typography variant="body1" paragraph>
                        SMS consent is captured with a dedicated checkbox, separate from
                        the Terms &amp; Conditions agreement. It is{" "}
                        <b>unchecked by default</b> and must be actively checked by the
                        user. The opt-in is <b>optional</b> and is never required to
                        create an account or use FundsyFi Recover. Below is a faithful,
                        non-interactive replica of that checkbox exactly as it appears to
                        the user:
                    </Typography>

                    <ConsentReplica>
                        <FormControlLabel
                            sx={{ alignItems: "flex-start", m: 0 }}
                            control={
                                <Checkbox
                                    checked={false}
                                    disabled
                                    disableRipple
                                    sx={{ pt: 0.25 }}
                                    inputProps={{
                                        "aria-label":
                                            "SMS consent checkbox, unchecked by default",
                                    }}
                                />
                            }
                            label={
                                <Typography variant="body2" sx={{ color: "#1f2937" }}>
                                    I agree to receive transactional SMS invoice and
                                    payment notifications from [Business Name] via FundsyFi
                                    Collect at the phone number provided. Messages may
                                    include invoice due reminders, past-due invoice
                                    notices, secure payment links, payment confirmations,
                                    and account-related payment updates. Message frequency
                                    varies based on invoice activity. Msg &amp; data rates
                                    may apply. Reply STOP to opt out, HELP for help. Consent
                                    is optional and is not required to pay an invoice or
                                    receive services. See our{" "}
                                    <Link href="https://www.fundsyfi.com/privacy-policy">
                                        Privacy Policy
                                    </Link>{" "}
                                    and{" "}
                                    <Link href="https://www.fundsyfi.com/terms-of-service">
                                        Terms of Service
                                    </Link>
                                    . (optional)
                                </Typography>
                            }
                        />
                    </ConsentReplica>
                </Section>

                <Section>
                    <Typography variant="h5" gutterBottom>
                        Screenshot of the live opt-in flow
                    </Typography>
                    <Typography variant="body1" paragraph>
                        The screenshot below shows the live FundsyFi opt-in form: a
                        labeled, optional phone-number field
                        (&ldquo;Optional &ndash; add your phone number to receive SMS
                        payment reminders&rdquo;) and a dedicated, <b>unchecked</b> SMS
                        consent checkbox carrying the full disclosure language,
                        message-frequency and rate disclosures, STOP/HELP instructions,
                        and links to the Privacy Policy and Terms of Service. The SMS
                        consent checkbox is separate from the Terms &amp; Conditions
                        checkbox.
                    </Typography>
                    <Figure>
                        <Screenshot
                            src="/images/sms-evidence/signup-phone-optin.png"
                            alt="FundsyFi Recover opt-in form showing the labeled optional phone-number field and a dedicated, unchecked SMS consent checkbox stating that the user agrees to receive transactional SMS invoice and payment notifications from [Business Name] via FundsyFi Recover at the phone number provided. The checkbox includes message frequency and rate disclosures, STOP and HELP instructions, and links to the Privacy Policy and Terms of Service. The SMS consent checkbox is separate from the Terms & Conditions checkbox."
                        />
                        <Typography variant="caption" color="text.secondary">
                            FundsyFi Recover opt-in form showing the labeled optional
                            phone-number field and a dedicated, unchecked SMS consent
                            checkbox stating that the user agrees to receive transactional
                            SMS invoice and payment notifications from [Business Name] via
                            FundsyFi Recover at the phone number provided. The checkbox
                            includes message frequency and rate disclosures, STOP and HELP
                            instructions, and links to the Privacy Policy and Terms of
                            Service. The SMS consent checkbox is separate from the Terms
                            &amp; Conditions checkbox.
                        </Typography>
                    </Figure>
                </Section>

                <Section>
                    <Typography variant="h5" gutterBottom>
                        Policy &amp; evidence URLs
                    </Typography>
                    <Box component="ul" sx={{ pl: 2 }}>
                        <li>
                            <Typography variant="body1">
                                Privacy Policy:{" "}
                                <Link href="https://www.fundsyfi.com/privacy-policy">
                                    https://www.fundsyfi.com/privacy-policy
                                </Link>
                            </Typography>
                        </li>
                        <li>
                            <Typography variant="body1">
                                Terms of Service:{" "}
                                <Link href="https://www.fundsyfi.com/terms-of-service">
                                    https://www.fundsyfi.com/terms-of-service
                                </Link>
                            </Typography>
                        </li>
                        <li>
                            <Typography variant="body1">
                                SMS Opt-In Evidence:{" "}
                                <Link href="https://www.fundsyfi.com/collect-sms-consent">
                                    https://www.fundsyfi.com/sms-opt-in-evidence
                                </Link>
                            </Typography>
                        </li>
                    </Box>
                </Section>

                <Section>
                    <Typography variant="h5" gutterBottom>
                        Required disclosures
                    </Typography>
                    <Box component="ul" sx={{ pl: 2 }}>
                        <li>
                            <Typography variant="body1">
                                <b>Message frequency varies</b> based on the recipient&rsquo;s
                                outstanding invoices and account activity.
                            </Typography>
                        </li>
                        <li>
                            <Typography variant="body1">
                                <b>Message and data rates may apply.</b>
                            </Typography>
                        </li>
                        <li>
                            <Typography variant="body1">
                                Carriers are not liable for delayed or undelivered messages.
                            </Typography>
                        </li>
                        <li>
                            <Typography variant="body1">
                                Mobile information is <b>never shared with third parties</b> or
                                affiliates for marketing, and opt-in data is never sold &mdash;
                                see{" "}
                                <Link href="https://www.fundsyfi.com/privacy-policy">
                                    Privacy Policy, Section 13
                                </Link>
                                .
                            </Typography>
                        </li>
                        <li>
                            <Typography variant="body1">
                                Full SMS program terms, including HELP and STOP instructions, are
                                published in the{" "}
                                <Link href="https://www.fundsyfi.com/terms-of-service">
                                    Terms of Service, Section 16 (SMS Program)
                                </Link>
                                .
                            </Typography>
                        </li>
                    </Box>
                </Section>

                <Section>
                    <Typography variant="h5" gutterBottom>
                        Opt-out and help (STOP / START / HELP)
                    </Typography>
                    <Typography variant="body1" paragraph>
                        Recipients can opt out at any time by replying <b>STOP</b> to any
                        message. FundsyFi processes opt-outs automatically and immediately
                        stops further messages to that number; the recipient may receive
                        one final confirmation. To opt back in, reply <b>START</b>. For
                        help, reply <b>HELP</b> or email{" "}
                        <Link href="mailto:support@fundsyfi.com">support@fundsyfi.com</Link>.
                    </Typography>
                </Section>

                <Section>
                    <Typography variant="h5" gutterBottom>
                        Sample messages
                    </Typography>
                    <Box component="ul" sx={{ pl: 2 }}>
                        <li>
                            <Typography variant="body1">
                                [Business Name] via FundsyFi Recover: Hi Jordan, invoice
                                #1042 for $450 is past due. Pay securely:
                                https://www.fundsyfi.com/pay/a1b2c3 Reply STOP to opt out,
                                HELP for help.
                            </Typography>
                        </li>
                        <li>
                            <Typography variant="body1">
                                [Business Name] via FundsyFi Recover: Reminder &mdash;
                                invoice #1042 for $450 is due tomorrow. Pay securely:
                                https://www.fundsyfi.com/pay/a1b2c3 Reply STOP to opt out,
                                HELP for help.
                            </Typography>
                        </li>
                        <li>
                            <Typography variant="body1">
                                [Business Name] via FundsyFi Recover: Payment of $450 for
                                invoice #1042 was received. Thank you. Reply STOP to opt
                                out, HELP for help.
                            </Typography>
                        </li>
                    </Box>
                </Section>

                <Section>
                    <Typography variant="h5" gutterBottom>
                        Contact
                    </Typography>
                    <Typography variant="body1">
                        <Link href="mailto:support@fundsyfi.com">support@fundsyfi.com</Link>
                    </Typography>
                    <Typography variant="body1">Houston, Texas 77085</Typography>
                </Section>
            </PageContainer>
            <Footer />
        </React.Fragment>
    );
};

export default CollectSmsConsent;
